import mixpanel from "mixpanel-browser";

interface EventProperties {
  [key: string]: any;
}

// Initialize is called in main.tsx
// This is just a reference for how initialization should be done
export const initMixpanel = (
  token: string,
  options = { track_pageview: true }
) => {
  mixpanel.init(token, options);
};

// Identify a user
export const identify = (id: string) => {
  mixpanel.identify(id);
};

// Alias for when a user first signs up
export const alias = (id: string) => {
  mixpanel.alias(id);
};

// Track an event
export const track = (event: string, properties?: EventProperties) => {
  mixpanel.track(event, properties);
};

// Set user properties
export const setUserProperties = (properties: EventProperties) => {
  mixpanel.people.set(properties);
};

// Track page view
export const trackPageView = (page: string) => {
  mixpanel.track("Page View", { page });
};

// Reset user data when they log out
export const reset = () => {
  mixpanel.reset();
};

// Create a consistent naming scheme for common events
export const EVENTS = {
  AUTH: {
    LOGIN_ATTEMPT: "Login Attempt",
    LOGIN_SUCCESS: "Logged In",
    LOGIN_FAILURE: "Login Failed",
    REGISTER_ATTEMPT: "Register Attempt",
    REGISTER_SUCCESS: "Register Success",
    REGISTER_FAILURE: "Register Failure",
    RESET_ATTEMPT: "Reset Password Attempt",
    RESET_SUCCESS: "Reset Password Success",
    RESET_FAILURE: "Reset Password Failure",
    LOGOUT_ATTEMPT: "Logout Attempt",
    LOGOUT_CONFIRMED: "Logout Confirmed",
    GOOGLE_LOGIN: "Google OAuth Login",
  },
  POSITION: {
    GROUPING_CHANGED: "Position Grouping Changed",
    TAG_FILTER_CHANGED: "Position Tag Filter Changed",
    VIEW_CHANGED: "Position View Changed",
    POSITION_DRAGGED: "Position Dragged",
    POSITION_SELECTED: "Position Selected",
    POSITION_DETAIL_VIEWED: "Position Detail Viewed",
    POSITION_DETAIL_LOADED: "Position Detail Loaded",
    POSITION_TAB_CHANGED: "Position Tab Changed",
    POSITION_FILTERED: "Position Filtered",
    POSITION_SORTED: "Position Sorted",
    POSITION_EXPORTED: "Position Exported",
    POSITION_CREATED: "Position Created",
    POSITION_EDITED: "Position Edited",
    POSITION_DELETED: "Position Deleted",
    TAG_ADDED: "Position Tag Added",
    TAG_REMOVED: "Position Tag Removed",
    GROUP_CREATED: "Position Group Created",
    GROUP_EDITED: "Position Group Edited",
    GROUP_DELETED: "Position Group Deleted",
  },
  TRADE: {
    NOTE_ADDED: "Trade Note Added",
    NOTE_EDITED: "Trade Note Edited",
    NOTE_DELETED: "Trade Note Deleted",
    TRANSACTION_DRAGGED: "Transaction Dragged",
    TRADE_SELECTED: "Trade Selected",
    TRADE_FILTERED: "Trade Filtered",
    TRADE_SORTED: "Trade Sorted",
    TRADE_CREATED: "Trade Created",
    TRADE_UPDATED: "Trade Updated",
    TRADE_DELETED: "Trade Deleted",
    BULK_EDITED: "Trades Bulk Edited",
    ENTRY_FORM_STARTED: "Trade Entry Form Started",
    ENTRY_FORM_COMPLETED: "Trade Entry Form Completed",
    ENTRY_FORM_ABANDONED: "Trade Entry Form Abandoned",
  },
  FILTER: {
    UNDERLYING_CHANGED: "Underlying Filter Changed",
    DATE_RANGE_APPLIED: "Date Range Filter Applied",
    TAG_FILTER_APPLIED: "Tag Filter Applied",
    POSITION_GROUP_FILTER_APPLIED: "Position Group Filter Applied",
    BOOK_FILTER_APPLIED: "Book Filter Applied",
    STRATEGY_FILTER_APPLIED: "Strategy Filter Applied",
    FILTER_CLEARED: "Filter Cleared",
    QUICK_FILTER_USED: "Quick Filter Used",
    FILTER_SAVED: "Filter Saved",
    FILTER_UPDATED: "Filter Updated",
    FILTER_DELETED: "Filter Deleted",
  },
  UPLOAD: {
    ATTEMPT: "Upload File Attempt",
    SUCCESS: "Upload File Success",
    FAILURE: "Upload File Failure",
    BULK_TRADES_CREATED: "Bulk Trades Created",
    BULK_TRADES_FAILED: "Bulk Trades Creation Failed",
    BULK_CASH_FLOWS_CREATED: "Bulk Cash Flows Created",
    BULK_CASH_FLOWS_FAILED: "Bulk Cash Flows Creation Failed",
    SEARCH_BROKER: "Search Broker",
    ADD_ASSETS_CLICKED: "Add Assets Button Clicked",
  },
  SUBSCRIPTION: {
    SUCCESS: "User successfully subscribed",
    CANCELLED: "User cancelled stripe transaction",
    REQUEST_PORTAL: "Request Stripe Portal",
  },
  SETTINGS: {
    CHANGE_PASSWORD_ATTEMPT: "Change Password Attempt",
    PROFILE_UPDATED: "Profile Updated",
    SETTING_CHANGED: "Setting Changed",
    DATE_FORMAT_CHANGED: "Date Format Changed",
    THEME_CHANGED: "Theme Changed",
  },
  // Dashboard events
  DASHBOARD: {
    INSIGHT_ADDED: "Dashboard Insight Added",
    INSIGHT_REMOVED: "Dashboard Insight Removed",
    INSIGHT_RESIZED: "Dashboard Insight Resized",
    INSIGHT_DRAGGED: "Dashboard Insight Dragged",
    DASHBOARD_CHANGED: "Dashboard Changed",
    LAYOUT_UPDATED: "Dashboard Layout Updated",
    DASHBOARD_DELETED: "Dashboard Deleted",
    DASHBOARD_RENAMED: "Dashboard Renamed",
    DASHBOARD_CREATED: "Dashboard Created",
  },
  // Cashflows events
  CASHFLOW: {
    FILTER_APPLIED: "Cashflow Filter Applied",
    ROW_SELECTED: "Cashflow Row Selected",
    CASHFLOW_EXPLAINED: "Cashflow Explained",
    TOGGLE_UNCATEGORISED: "Toggle Uncategorised Cashflows",
    TOGGLE_UNBOOKED: "Toggle Unbooked Cashflows",
    TOGGLE_NO_POSITION_GROUPING: "Toggle No Position Grouping Cashflows",
    DATE_RANGE_CHANGED: "Cashflow Date Range Changed",
  },
  // Calendar events
  CALENDAR: {
    NOTE_CREATED: "Calendar Note Created",
    NOTE_VIEWED: "Calendar Note Viewed",
    MONTH_CHANGED: "Calendar Month Changed",
    DAY_SELECTED: "Calendar Day Selected",
  },
  // Toolbar events
  TOOLBAR: {
    INSIGHT_BUTTON_CLICKED: "Insight Button Clicked",
    HELP_OPENED: "Help Opened",
    FILTER_BUTTON_CLICKED: "Filter Button Clicked",
    TOOLBAR_FEATURE_USED: "Toolbar Feature Used",
    CLEAR_FILTERS_CLICKED: "Clear Filters Clicked",
    DASHBOARD_SELECTOR_USED: "Dashboard Selector Used",
    AUTOSYNC_BUTTON_CLICKED: "AutoSync Button Clicked",
    PRICING_QUEUE_CHECKED: "Pricing Queue Checked",
  },
  // Note events
  NOTES: {
    CREATED: "Note Created",
    EDITED: "Note Edited",
    DELETED: "Note Deleted",
    VIEWED: "Note Viewed",
    FORMATTING_APPLIED: "Note Formatting Applied",
    LINK_ADDED: "Link Added to Note",
    IMAGE_ADDED: "Image Added to Note",
  },
  // AutoSync events
  AUTOSYNC: {
    BROKER_CONNECTED: "Broker Connected",
    BROKER_DISCONNECTED: "Broker Disconnected",
    SYNC_STARTED: "Sync Started",
    SYNC_COMPLETED: "Sync Completed",
    SYNC_FAILED: "Sync Failed",
    SYNC_SCHEDULED: "Sync Scheduled",
    SCHEDULE_CHANGED: "Sync Schedule Changed",
    MANUAL_SYNC_TRIGGERED: "Manual Sync Triggered",
  },
  // API Keys
  API_KEYS: {
    CREATED: "API Key Created",
    DELETED: "API Key Deleted",
    VIEWED: "API Keys Viewed",
    COPIED: "API Key Copied",
  },
  // Data Visualization
  VISUALIZATION: {
    CHART_INTERACTED: "Chart Interaction",
    CHART_ZOOMED: "Chart Zoomed",
    CHART_PANNED: "Chart Panned",
    TOOLTIP_VIEWED: "Chart Tooltip Viewed",
    DATA_EXPORTED: "Visualization Data Exported",
    CUSTOM_CONFIG_SAVED: "Custom Chart Configuration Saved",
    VISUALIZATION_SHARED: "Visualization Shared",
  },
  // Performance Analysis
  PERFORMANCE: {
    REPORT_GENERATED: "Performance Report Generated",
    METRIC_SELECTED: "Performance Metric Selected",
    BENCHMARK_COMPARED: "Benchmark Comparison Made",
    TIME_PERIOD_CHANGED: "Performance Time Period Changed",
    PERFORMANCE_EXPORTED: "Performance Data Exported",
    PERFORMANCE_SHARED: "Performance Report Shared",
  },
  // Import/Export
  DATA_MANAGEMENT: {
    IMPORT_STARTED: "Data Import Started",
    IMPORT_COMPLETED: "Data Import Completed",
    IMPORT_FAILED: "Data Import Failed",
    EXPORT_STARTED: "Data Export Started",
    EXPORT_COMPLETED: "Data Export Completed",
    EXPORT_FAILED: "Data Export Failed",
    DATA_BACKED_UP: "Data Backed Up",
    DATA_RESTORED: "Data Restored",
  },
  // Risk Management
  RISK: {
    RISK_CALCULATION_VIEWED: "Risk Calculation Viewed",
    PORTFOLIO_RISK_ANALYZED: "Portfolio Risk Analyzed",
    POSITION_RISK_ANALYZED: "Position Risk Analyzed",
    RISK_REPORT_GENERATED: "Risk Report Generated",
    RISK_PARAMETERS_CHANGED: "Risk Parameters Changed",
    VAR_CALCULATED: "Value at Risk Calculated",
  },
  // Tutorial & Onboarding
  ONBOARDING: {
    TUTORIAL_STARTED: "Tutorial Started",
    TUTORIAL_STEP_COMPLETED: "Tutorial Step Completed",
    TUTORIAL_COMPLETED: "Tutorial Completed",
    TUTORIAL_SKIPPED: "Tutorial Skipped",
    HELP_TOOLTIP_SHOWN: "Help Tooltip Shown",
    HELP_TOOLTIP_CLICKED: "Help Tooltip Clicked",
    FEATURE_DISCOVERED: "Feature Discovered",
  },
  // Error Handling
  ERROR: {
    API_ERROR: "API Error Occurred",
    UI_ERROR: "UI Error Occurred",
    VALIDATION_FAILED: "Form Validation Failed",
    ERROR_REPORTED: "Error Reported by User",
    ERROR_RECOVERED: "Error Recovery Path Taken",
  },
  // Search Functionality
  SEARCH: {
    SEARCH_PERFORMED: "Search Performed",
    SEARCH_RESULTS_VIEWED: "Search Results Viewed",
    SEARCH_REFINED: "Search Query Refined",
    SEARCH_RESULT_CLICKED: "Search Result Clicked",
    ADVANCED_SEARCH_USED: "Advanced Search Used",
    NO_RESULTS_FOUND: "No Search Results Found",
  },
  // Navigation
  NAVIGATION: {
    PAGE_NAVIGATED: "Page Navigated",
    SIDEBAR_TOGGLE: "Sidebar Toggled",
    MODAL_OPENED: "Modal Opened",
    MODAL_CLOSED: "Modal Closed",
    DRAWER_OPENED: "Drawer Opened",
    DRAWER_CLOSED: "Drawer Closed",
    TAB_CHANGED: "Tab Changed",
    HELP_DOCUMENTATION_ACCESSED: "Help Documentation Accessed",
  },
};
